import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ChevronsLeftRight, AlertCircle, Star, Leaf, ExternalLink, MapPin, Search, X } from "lucide-react";

const NICE_KEY = (k) =>
  k.replace(/_/g, " ").replace(/\bpct\b/i, "%").replace(/\bm3\b/i, "m³").replace(/\bm2\b/i, "m²");

const NICE_VALUE = (v) => {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
};

function HeroStat({ label, value }) {
  return (
    <div className="border-l-2 border-accent pl-4 py-1">
      <div className="font-display text-3xl font-bold tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function ProjectCard({ p, onOpen }) {
  return (
    <button
      onClick={() => onOpen(p)}
      className="card-flat overflow-hidden text-left group cursor-pointer hover:border-accent transition-colors"
      data-testid={`pub-card-${p.id}`}
    >
      <div className="aspect-[4/3] bg-surface relative overflow-hidden">
        <img
          src={p.image_url}
          alt={p.title}
          loading="lazy"
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {p.featured && (
          <div className="absolute top-3 left-3">
            <Badge variant="lime"><Star className="h-3 w-3 mr-1 fill-current" /> Featured</Badge>
          </div>
        )}
        <div className="absolute top-3 right-3">
          <Badge variant="outline" className="bg-background/80 backdrop-blur">{p.category}</Badge>
        </div>
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/95 via-background/70 to-transparent p-4">
          <div className="font-display font-semibold text-base leading-tight tracking-tight mb-1">{p.title}</div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            {p.client || "—"}{p.year_completed ? ` · ${p.year_completed}` : ""}
          </div>
        </div>
      </div>
    </button>
  );
}

function Lightbox({ project, onClose }) {
  React.useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", h); document.body.style.overflow = ""; };
  }, [onClose]);
  if (!project) return null;
  const p = project;
  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="min-h-screen flex items-center justify-center p-4">
        <div
          className="card-flat max-w-4xl w-full overflow-hidden grid grid-cols-1 lg:grid-cols-2"
          onClick={(e) => e.stopPropagation()}
          data-testid="pub-lightbox"
        >
          <div className="bg-surface relative">
            <img src={p.image_url} alt={p.title} className="w-full h-full object-cover max-h-[80vh]" referrerPolicy="no-referrer" />
          </div>
          <div className="p-6 lg:p-8 flex flex-col">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <div className="label-eyebrow mb-2">{p.category}</div>
                <h2 className="font-display text-2xl font-bold tracking-tight">{p.title}</h2>
              </div>
              <button onClick={onClose} className="p-1 hover:bg-surface-hover rounded-sm" data-testid="pub-lightbox-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="text-xs text-muted-foreground mb-4 flex items-center gap-1.5">
              {p.location && <><MapPin className="h-3 w-3" /> {p.location}</>}
              {p.year_completed && <span>· Completed {p.year_completed}</span>}
            </div>
            {p.client && (
              <div className="mb-3"><span className="label-eyebrow">Client</span><div className="text-sm">{p.client}</div></div>
            )}
            {p.description && <p className="text-sm text-muted-foreground leading-relaxed mb-4">{p.description}</p>}
            {p.esg_highlights && Object.keys(p.esg_highlights).length > 0 && (
              <div className="card-flat p-4 mb-4 bg-surface">
                <div className="flex items-center gap-2 mb-2 label-eyebrow"><Leaf className="h-3 w-3 text-emerald-400" /> ESG highlights</div>
                <dl className="space-y-1.5">
                  {Object.entries(p.esg_highlights).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs font-mono">
                      <dt className="text-muted-foreground">{NICE_KEY(k)}</dt>
                      <dd className="font-medium text-foreground">{NICE_VALUE(v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            {p.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {p.tags.map((t) => <Badge key={t} variant="outline">{t}</Badge>)}
              </div>
            )}
            {p.source_url && (
              <a href={p.source_url} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline flex items-center gap-1 mt-auto">
                <ExternalLink className="h-3 w-3" /> View original project page
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PublicGallery() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);

  useEffect(() => { document.documentElement.classList.add("dark"); }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery/public/${slug}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setData)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const categories = useMemo(() => {
    if (!data) return [];
    const s = new Set(data.projects.map(p => p.category).filter(Boolean));
    return ["ALL", ...Array.from(s).sort()];
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.projects.filter((p) => {
      if (filter !== "ALL" && p.category !== filter) return false;
      if (!q) return true;
      return (p.title + " " + (p.client || "") + " " + (p.description || "") + " " + (p.tags || []).join(" "))
        .toLowerCase().includes(q);
    });
  }, [data, filter, search]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground dark flex items-center justify-center font-mono text-sm text-muted-foreground">
        Loading gallery…
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-background text-foreground dark flex items-center justify-center p-6">
        <div className="card-flat p-12 max-w-md text-center">
          <AlertCircle className="h-10 w-10 mx-auto mb-3 text-red-400" />
          <h2 className="font-display text-2xl font-bold mb-2">Gallery not found</h2>
          <p className="text-sm text-muted-foreground">This showcase link is invalid or the organisation has not published a gallery.</p>
        </div>
      </div>
    );
  }

  const { tenant, totals, projects } = data;
  const featured = projects.filter(p => p.featured).slice(0, 1)[0];

  // Pretty hero stats (max 4 numeric totals)
  const heroStats = Object.entries(totals || {})
    .filter(([, v]) => typeof v === "number" && v >= 1)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-background text-foreground dark" data-testid="public-gallery-page">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-30 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-accent flex items-center justify-center rounded-sm">
              <ChevronsLeftRight className="h-5 w-5 text-black" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight text-base" data-testid="pub-tenant-name">{tenant.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{tenant.industry_name || "Sustainability portfolio"} · {tenant.country}</div>
            </div>
          </div>
          {tenant.website && (
            <a href={tenant.website} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5">
              <ExternalLink className="h-3.5 w-3.5" /> {tenant.website.replace(/^https?:\/\//, "")}
            </a>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border relative overflow-hidden">
        {featured?.image_url && (
          <div className="absolute inset-0 -z-10 opacity-20">
            <img src={featured.image_url} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/90 to-background/40" />
          </div>
        )}
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14 lg:py-20">
          <div className="label-eyebrow mb-3">Project showcase · {projects.length} initiatives</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl leading-[1.05]" data-testid="pub-hero-title">
            {tenant.tagline || `Sustainable work by ${tenant.name}`}
          </h1>
          <p className="text-base text-muted-foreground mt-5 max-w-2xl">
            A living portfolio of green & ESG-relevant projects — curated and published through the Lestari Control Tower.
          </p>

          {heroStats.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-6 mt-10 max-w-3xl" data-testid="pub-hero-stats">
              {heroStats.map(([k, v]) => (
                <HeroStat key={k} label={NICE_KEY(k)} value={Number(v).toLocaleString()} />
              ))}
            </div>
          )}

          {tenant.services?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-10 max-w-3xl">
              {tenant.services.map((s) => <Badge key={s} variant="outline">{s}</Badge>)}
            </div>
          )}
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-border sticky top-[68px] bg-card/95 backdrop-blur z-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex flex-wrap gap-1.5">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`pub-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
                  filter === c
                    ? "bg-accent text-accent-foreground border-accent"
                    : "border-border text-muted-foreground hover:text-foreground hover:bg-surface-hover"
                }`}
              >
                {c}
                {c !== "ALL" && <span className="ml-1.5 opacity-50">{projects.filter(p => p.category === c).length}</span>}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search projects…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-56"
              data-testid="pub-search"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-10 lg:py-14">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground font-mono text-sm">
            No projects match your search.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pub-gallery-grid">
            {filtered.map((p) => (
              <ProjectCard key={p.id} p={p} onOpen={setActive} />
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 flex items-center justify-between flex-wrap gap-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Published with <span className="text-foreground font-display font-semibold">Lestari</span> · ESG Control Tower
          </div>
          {tenant.website && (
            <a href={tenant.website} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-foreground">
              {tenant.name} →
            </a>
          )}
        </div>
      </footer>

      <Lightbox project={active} onClose={() => setActive(null)} />
    </div>
  );
}
